import { useEffect, useRef, useState } from 'react';
import { NOTEBOOK_WRITING_ACTIONS, notebookWritingReplacesByDefault } from '@harvus/shared';
import { B, radius } from '../constants/brand';
import { api } from '../api/client';
import { Btn, Icon } from '../components/ui';

// =============================================================
// WritingTools — Harvi's version of Apple's Writing Tools, for the Notes app.
//
// Select text (or select nothing for the whole note), pick an action, and the
// result appears in a panel BESIDE the note — never straight into it. The
// person then chooses Replace / Insert below / Copy. A model rewriting a note
// in place, unreviewed, is the one thing this must not do: notes carry figures
// people act on.
//
// The selection is captured when the menu opens (the button keeps the editor's
// focus via onMouseDown preventDefault), as BLOCK ids — BlockNote replaces
// whole blocks, so a partial selection inside one paragraph acts on that
// paragraph. If those blocks are edited away before Replace, the person is
// told and can still Insert or Copy.
//
// API: POST /api/notebook-writing (api/src/functions/notebookWriting.js).
// =============================================================

// `extraGroups` adds menu groups that are not writing actions — the Notes app
// puts "File this note" (Suggest links & tasks · Split & route) here: [{ id,
// title, items: [{ key, label, onSelect }] }]. They act on the whole note.
export default function WritingTools({ editorRef, toast, disabled, extraGroups = [] }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null); // { action, label, markdown, targetIds, scope }
  const target = useRef(null);
  const wrap = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const captureTarget = () => {
    const editor = editorRef.current;
    if (!editor) return null;
    let blocks = [];
    try { blocks = editor.getSelection()?.blocks || []; } catch { blocks = []; }
    const scope = blocks.length ? 'selection' : 'note';
    if (!blocks.length) blocks = editor.document;
    return { scope, blocks };
  };

  const run = async (a) => {
    const editor = editorRef.current;
    const t = target.current || captureTarget();
    setOpen(false);
    if (!editor || !t) return;
    let markdown = '';
    try { markdown = await editor.blocksToMarkdownLossy(t.blocks); } catch { markdown = ''; }
    if (!markdown.trim()) { toast('There is no text to work on yet.', 'error'); return; }
    setBusy(a.key);
    try {
      const res = await api.post('notebook-writing', { action: a.key, text: markdown }, { timeout: 120000 });
      setResult({ action: a.key, label: a.label, markdown: res.result, targetIds: t.blocks.map(b => b.id), scope: t.scope });
    } catch (e) {
      toast(e.message || 'Harvi could not do that.', 'error');
    } finally { setBusy(null); }
  };

  const apply = async (mode) => {
    const editor = editorRef.current;
    if (!editor || !result) return;
    let blocks = [];
    try { blocks = await editor.tryParseMarkdownToBlocks(result.markdown); } catch { blocks = []; }
    if (!blocks.length) blocks = result.markdown.split('\n').map(line => ({ type: 'paragraph', content: line }));
    const live = result.targetIds.filter(id => editor.getBlock(id));
    try {
      if (mode === 'replace') {
        if (live.length !== result.targetIds.length) { toast('The text changed since you asked — use Insert below instead.', 'error'); return; }
        editor.replaceBlocks(live, blocks);
      } else {
        const anchor = live[live.length - 1] || editor.document[editor.document.length - 1];
        editor.insertBlocks(blocks, anchor, 'after');
      }
      setResult(null);
    } catch (e) { toast(e.message || 'Could not put that in the note.', 'error'); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result.markdown); toast('Copied.'); } catch { toast('Could not copy.', 'error'); }
  };

  const groups = [
    { id: 'edit', title: 'Edit' },
    { id: 'summarize', title: 'Summarize & organize' },
  ];

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <button type="button" aria-label="Harvi writing tools" title="Harvi writing tools — select text first, or use the whole note"
        disabled={disabled || !!busy}
        onMouseDown={(e) => { e.preventDefault(); target.current = captureTarget(); }}
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 10px', borderRadius: 8,
          border: `1px solid ${open ? B.navy : 'transparent'}`, background: open ? '#e6eef7' : 'transparent',
          color: B.navy, fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
        }}>
        <Icon name="sparkles" size={16} /> {busy ? 'Working…' : 'Harvi'}
      </button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', right: 0, top: 36, zIndex: 50, width: 240, background: B.white, borderRadius: radius.md,
          boxShadow: B.shadowLg || '0 8px 24px rgba(0,0,0,0.12)', border: `1px solid ${B.border}`, padding: 6,
        }}>
          <div style={{ fontSize: 11, color: B.textLt, padding: '4px 8px 6px' }}>
            {target.current?.scope === 'selection' ? 'On the selected text' : 'On the whole note'}
          </div>
          {groups.map(g => (
            <div key={g.id}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B.textMd, padding: '6px 8px 2px', textTransform: 'uppercase', letterSpacing: 0.4 }}>{g.title}</div>
              {NOTEBOOK_WRITING_ACTIONS.filter(a => a.group === g.id).map(a => (
                <button key={a.key} role="menuitem" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => run(a)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '7px 8px', borderRadius: 6, fontSize: 13, color: B.text, cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = B.surfaceMuted; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                  {a.label}
                </button>
              ))}
            </div>
          ))}
          {extraGroups.map(g => (
            <div key={g.id} style={{ borderTop: `1px solid ${B.divider}`, marginTop: 4, paddingTop: 2 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B.textMd, padding: '6px 8px 2px', textTransform: 'uppercase', letterSpacing: 0.4 }}>{g.title}</div>
              {g.items.map(it => (
                <button key={it.key} role="menuitem" type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { setOpen(false); it.onSelect(); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '7px 8px', borderRadius: 6, fontSize: 13, color: B.text, cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = B.surfaceMuted; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                  {it.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
      {result && (
        <div role="dialog" aria-label={`Harvi: ${result.label}`} style={{
          position: 'absolute', right: 0, top: 36, zIndex: 40, width: 'min(520px, calc(100vw - 32px))', background: B.white,
          borderRadius: radius.md, border: `1px solid ${B.border}`, boxShadow: B.shadowLg || '0 8px 24px rgba(0,0,0,0.12)', padding: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Icon name="sparkles" size={14} color={B.navy} />
            <strong style={{ fontSize: 13, color: B.text }}>{result.label}</strong>
            <span style={{ fontSize: 12, color: B.textLt }}>{result.scope === 'selection' ? 'of the selection' : 'of the whole note'}</span>
            <span style={{ flex: 1 }} />
            <button type="button" aria-label="Discard" onClick={() => setResult(null)} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: B.textMd }}>
              <Icon name="close" size={14} />
            </button>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.55, color: B.text, background: B.surfaceMuted, borderRadius: 8, padding: 10 }}>
            {result.markdown}
          </div>
          <div style={{ fontSize: 11, color: B.textLt, margin: '8px 0' }}>Written by Harvi — check the figures before you keep it.</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Btn size="sm" variant="secondary" onClick={copy}>Copy</Btn>
            <Btn size="sm" variant={notebookWritingReplacesByDefault(result.action) ? 'secondary' : 'primary'} onClick={() => apply('insert')}>Insert below</Btn>
            <Btn size="sm" variant={notebookWritingReplacesByDefault(result.action) ? 'primary' : 'secondary'} onClick={() => apply('replace')}>Replace</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
